import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import api from "../lib/api";
import ModuleAccessDialog from "../components/ModuleAccessDialog";
import { useAuth } from "../context/AuthContext";
import {
  ShieldCheck, Search, AlertTriangle, Clock, CheckCircle2, Users as UsersIcon, RefreshCw, ChevronRight, Layers,
} from "lucide-react";

const STAGE_LABELS = ["Account", "KYC", "Accredited", "Funded"];
const LIGHT_BG = {
  green:  "bg-emerald-500 ring-emerald-500/30",
  yellow: "bg-amber-400 ring-amber-400/30",
  red:    "bg-rose-500 ring-rose-500/30",
};

const FILTERS = [
  { key: "all",            label: "All users",         icon: UsersIcon },
  { key: "stuck",          label: "Stuck (any red)",   icon: AlertTriangle },
  { key: "awaiting_admin", label: "Awaiting your action", icon: Clock },
  { key: "all_green",      label: "Fully cleared",     icon: CheckCircle2 },
];

const CompliancePipelinePage = () => {
  const { user } = useAuth();
  const isSuperuser = user?.role === "superuser";
  const [data, setData] = useState({ users: [], count: 0, stats: { all_green: 0, any_yellow: 0, any_red: 0, awaiting_admin: 0 } });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [activeUser, setActiveUser] = useState(null);
  const [activeDetail, setActiveDetail] = useState(null);
  const [moduleOpen, setModuleOpen] = useState(false);

  const load = useCallback(async (f = filter) => {
    setLoading(true);
    try {
      const r = await api.get(`/compliance/admin/list?filter=${f}&limit=300`);
      setData(r.data);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(filter); }, [filter, load]);

  const openDetail = async (row) => {
    setActiveUser(row);
    setActiveDetail(null);
    const r = await api.get(`/compliance/admin/${row.id}`);
    setActiveDetail(r.data);
  };

  const filtered = data.users.filter((u) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.full_name || "").toLowerCase().includes(q) ||
           (u.email || "").toLowerCase().includes(q) ||
           (u.internal_email || "").toLowerCase().includes(q);
  });

  return (
    <DashboardLayout
      title="Compliance Pipeline"
      subtitle="Per-user traffic-light view of every onboarding stage"
    >
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="All cleared"        value={data.stats.all_green}      light="green"  testid="stat-all-green" />
        <StatCard label="Awaiting your action" value={data.stats.awaiting_admin} light="yellow" testid="stat-awaiting-admin" />
        <StatCard label="Any yellow"          value={data.stats.any_yellow}     light="yellow" testid="stat-any-yellow" />
        <StatCard label="Any red (stuck)"     value={data.stats.any_red}        light="red"    testid="stat-any-red" />
      </div>

      {/* Filter tabs + search */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="bg-slate-100">
            {FILTERS.map((f) => (
              <TabsTrigger key={f.key} value={f.key} className="gap-1.5" data-testid={`filter-${f.key}`}>
                <f.icon size={14} /> {f.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="pl-8 bg-white"
            data-testid="pipeline-search"
          />
        </div>
        <Button onClick={() => load(filter)} variant="outline" size="sm" className="gap-1.5" data-testid="pipeline-refresh">
          <RefreshCw size={14} /> Refresh
        </Button>
      </div>

      {/* Grid */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-slate-400 text-sm">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-400 text-sm" data-testid="pipeline-empty">
              No users match this filter.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="pipeline-table">
                <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-semibold">User</th>
                    {STAGE_LABELS.map((l) => (
                      <th key={l} className="px-2 py-3 font-semibold text-center">{l}</th>
                    ))}
                    <th className="px-4 py-3 font-semibold">Next action</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((u) => (
                    <tr key={u.id} className="hover:bg-slate-50 transition-colors" data-testid={`pipeline-row-${u.id}`}>
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-slate-900">{u.full_name}</p>
                        <p className="text-xs text-slate-500">{u.email}</p>
                        {u.internal_email && (
                          <p className="text-[11px] text-blue-700 font-mono">{u.internal_email}</p>
                        )}
                        <Badge variant="outline" className="mt-1 text-[10px]">{u.role}</Badge>
                      </td>
                      {u.lights.map((light, i) => (
                        <td key={`${u.id}-stage-${i}`} className="px-2 py-3 text-center" title={u.stage_summaries[i]}>
                          <span className={`inline-flex h-3.5 w-3.5 rounded-full ${LIGHT_BG[light]} ring-4`} data-testid={`row-${u.id}-stage-${i}`} />
                        </td>
                      ))}
                      <td className="px-4 py-3 text-xs text-slate-600 max-w-xs truncate">
                        {u.next_action_label || "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button onClick={() => openDetail(u)} variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 hover:bg-blue-50" data-testid={`pipeline-detail-${u.id}`}>
                          Details <ChevronRight size={12} className="ml-1" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail drawer */}
      {activeUser && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex justify-end" onClick={() => setActiveUser(null)} data-testid="pipeline-detail-drawer">
          <div className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-100 flex items-start justify-between gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Compliance Detail</p>
                <h2 className="text-xl font-serif text-slate-900">{activeUser.full_name}</h2>
                <p className="text-sm text-slate-500">{activeUser.email}</p>
              </div>
              <Button onClick={() => setActiveUser(null)} variant="ghost" size="sm">Close ✕</Button>
            </div>

            <div className="p-6 space-y-5">
              {!activeDetail ? (
                <div className="h-32 bg-slate-100 animate-pulse rounded" />
              ) : (
                <>
                  {/* 4 stage cards */}
                  <div className="space-y-2">
                    {activeDetail.stages.map((s, i) => (
                      <div key={s.key} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200">
                        <span className={`inline-flex h-3 w-3 rounded-full ${LIGHT_BG[s.light]} ring-4 shrink-0`} />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900">0{i + 1}. {s.label}</p>
                          <p className="text-xs text-slate-500">{s.summary}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Doc checklist */}
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-2">Documents</p>
                    <ul className="space-y-2">
                      {activeDetail.kyc_docs.map((d) => (
                        <li key={d.type} className="flex items-center justify-between p-2 rounded border border-slate-100 text-sm">
                          <div>
                            <p className="font-medium text-slate-900">
                              {d.label}
                              {d.required && <span className="ml-2 text-[10px] uppercase text-rose-600 font-semibold">required</span>}
                            </p>
                            {d.file_name && (
                              <p className="text-[11px] text-slate-500">{d.file_name}</p>
                            )}
                          </div>
                          <Badge className={
                            d.state === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                            d.state === "pending"  ? "bg-amber-50 text-amber-700 border-amber-200" :
                            d.state === "rejected" ? "bg-rose-50 text-rose-700 border-rose-200" :
                                                     "bg-slate-50 text-slate-500 border-slate-200"
                          }>
                            {d.state}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Quick links */}
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
                    <Button asChild variant="outline" size="sm" className="gap-1.5">
                      <Link to="/kyc"><ShieldCheck size={12} /> Review KYC docs</Link>
                    </Button>
                    <Button asChild variant="outline" size="sm">
                      <Link to="/users">User management</Link>
                    </Button>
                    {isSuperuser && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50"
                        onClick={() => setModuleOpen(true)}
                        data-testid="drawer-module-access"
                      >
                        <Layers size={12} /> Module access
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Module Access Dialog — triggered from drawer */}
      <ModuleAccessDialog
        open={moduleOpen}
        onOpenChange={setModuleOpen}
        targetUser={activeUser}
      />
    </DashboardLayout>
  );
};

const StatCard = ({ label, value, light, testid }) => (
  <Card className="bg-white border-slate-200" data-testid={testid}>
    <CardContent className="p-4 flex items-center gap-3">
      <span className={`inline-flex h-3 w-3 rounded-full ${LIGHT_BG[light]} ring-4 shrink-0`} />
      <div>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </CardContent>
  </Card>
);

export default CompliancePipelinePage;
